/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The library's reach, provided once and read at every depth** (`E4`, `#1272`; moved here by
 * `X30c`, `#1303`).
 *
 * A value at any depth may address a shared expression, which needs two answers — may this surface
 * use the library at all, and which context its values resolve against. Both are the **surface's**
 * answers, so they arrive on `DecoratorExpressionEditorProps` where the compiler makes every call
 * site give them (`#1134`), and they reach a value nested three components down through this context
 * rather than through three components that would only be passing them on.
 *
 * **It lives in its own module because the reader moved.** It was `BranchLibraryContext`, declared
 * beside the branch form that read it; `X30c` gives the shared slot to `ExpressionValueLine`, which
 * the editor imports — so a context declared in the editor would be one the value line reaches
 * through a cycle. The name lost *Branch* in the same move: it was never about branches, it was about
 * whatever is rendering a value.
 *
 * The default is *no library notion here*, which is the honest reading of *rendered outside the
 * editor*: it declines rather than claiming a capability the surface never answered for.
 */

import React from 'react';
import type { CapabilityRefused } from './MappingControl';
import type { ResolveSurfaces } from './shared-expression-usability';

export interface ExpressionLibraryAccess {
  offersLibrary: true | CapabilityRefused | null;
  resolvesAgainst: ResolveSurfaces | null;
}

export const ExpressionLibraryAccessContext = React.createContext<ExpressionLibraryAccess>({
  offersLibrary: null,
  resolvesAgainst: null,
});

export const useExpressionLibraryAccess = (): ExpressionLibraryAccess =>
  React.useContext(ExpressionLibraryAccessContext);
